import { useMemo } from 'react';
import { DomainStatsWithComparison } from '../../../shared/types/monitor/data/sources/DomainStatsWithComparison';
import { DomainsStatsWithShare } from './UseDomainsStatsShare';
import { useDomainsStatsSorted } from './UseDomainsStatsSorted';

/**
 * Hook to compare domains stats between two periods
 * Calculates changes (count changes, share changes) between current and previous period
 * Requires shares to be pre-calculated using useDomainsStatsShare
 */
export const useDomainsStatsComparison = (
  currentStatsWithShare: DomainsStatsWithShare | undefined,
  previousStatsWithShare: DomainsStatsWithShare | undefined
): DomainStatsWithComparison[] => {
  // Use sorted hook to ensure consistent sorting
  const sortedDomains = useDomainsStatsSorted(currentStatsWithShare);

  return useMemo(() => {
    if (!sortedDomains || sortedDomains.length === 0) {
      return [];
    }

    // Create a map of previous period data for fast lookup
    const previousStatsMap = new Map(
      previousStatsWithShare?.domains?.map(domain => [domain.domain, domain]) || []
    );

    // Domains are already sorted by count (descending) from useDomainsStatsSorted
    // Backend already returns only top 5 (pageSize=5), so no need to slice
    const topDomains = sortedDomains;

    return topDomains.map(currentDomain => {
      const currentCount = currentDomain.count;
      const currentShare = currentDomain.share;

      const previousDomain = previousStatsMap.get(currentDomain.domain);
      // If domain didn't exist in previous period, set as 0
      const previousCount = previousDomain?.count || 0;
      const previousShare = previousDomain?.share || 0;

      // Calculate changes - now we always have values to compare
      const countChange = currentCount - previousCount;
      const countChangePercent = previousCount > 0
        ? ((currentCount - previousCount) / previousCount) * 100
        : previousCount === 0 && currentCount > 0
        ? 100 // If it was 0 and now is > 0, that's 100% growth
        : 0;
      const shareChange = currentShare - previousShare;

      return {
        domain: currentDomain.domain,
        currentCount,
        currentShare,
        previousCount,
        previousShare,
        countChange,
        countChangePercent,
        shareChange,
      };
    });
  }, [sortedDomains, previousStatsWithShare]);
};
